import { FC, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useGetDataPoints } from 'src/app/indexedDB/dataPointsDB/hooks/useGetDataPoints';
import { useGetDataPointTree } from 'src/app/indexedDB/dataPointsTreeDB/hooks/useGetDataPointTree';
import { useGetDictionaries } from 'src/app/indexedDB/dictionaryDB/hooks/useGetDictionaries';
import { useAppSelector } from 'src/app/redux/createAction';
import { ScrollToTop } from 'src/app/router/ui/ScrollToTop';
import { BenchmarkPageLazy } from 'src/pages/BenchmarkPage';
import { CountryPageLazy } from 'src/pages/CountryPage';
import { DatabasePageLazy } from 'src/pages/DatabasePage';
import { FundPageLazy } from 'src/pages/funds_pages';
import { MainSearchPageLazy } from 'src/pages/MainPage';
import { MainSearchResultsPageLazy } from 'src/pages/MainSearchResultsPage';
import { NotFoundPage } from 'src/pages/NotFoundPage/NotFoundPage';
import {
	PortfolioCreatePageLazy, //
	PortfolioPageLazy,
	PortfolioViewPageLazy,
} from 'src/pages/portfolio_analyzer_pages';
import { ResetPasswordPage } from 'src/pages/ResetPasswordPage';
import { SignInPage } from 'src/pages/SignInPage';
import { WelcomePageLazy } from 'src/pages/WelcomePage';
import { renderRoute } from '../lib/renderRoute';
import { useGetDataPointTreeBoolean } from 'src/app/indexedDB/dataPointsTreeBooleanDB/hooks/useGetDataPointTreeBoolean';

export const routerPath = {
	mainWelcome: {
		page: '/welcome' as const,
	},
	mainSearch: {
		page: '/main' as const,
	},
	mainSearchResults: {
		page: '/search_results' as const,
	},
	database: {
		page: '/database' as const,
	},
	benchmark: {
		page: '/benchmark' as const,
	},
	portfolio: {
		page: '/portfolio_analyzer' as const,
		portfolio: '/portfolio' as const,
		portfolioList: '/portfolio_list' as const,
		upload: '/upload' as const,
	},
	funds: {
		page: '/funds' as const,
		fund: '/fund' as const,
	},
};

export const AppRouter: FC = () => {
	const userData = useAppSelector((state) => state.auth.userData);

	const dictionaries = useGetDictionaries();
	const dataPoints = useGetDataPoints();
	const dataPointTree = useGetDataPointTree();
	const dataPointTreeBoolean = useGetDataPointTreeBoolean();

	const coreDataExists = dictionaries && dataPoints && dataPointTree && dataPointTreeBoolean;
	const coreData = coreDataExists
		? {
				dictionaries,
				dataPoints,
				dataPointTree,
				dataPointTreeBoolean,
			}
		: null;

	// * Render
	return (
		<BrowserRouter>
			<ScrollToTop />

			<Suspense fallback={<div>Loading...</div>}>
				<Routes>
					<Route
						path="/"
						element={
							userData ? (
								<Navigate
									to={routerPath.mainWelcome.page}
									replace
								/>
							) : (
								<SignInPage />
							)
						}
					/>

					<Route
						path="/login/password"
						element={<ResetPasswordPage />}
					/>

					{renderRoute(routerPath.mainWelcome.page, <WelcomePageLazy />, userData, coreData)}
					{renderRoute(routerPath.mainSearch.page, <MainSearchPageLazy />, userData, coreData)}
					{renderRoute(`${routerPath.mainSearchResults.page}/:tab`, <MainSearchResultsPageLazy />, userData, coreData)}

					{/* Database */}
					{renderRoute(`${routerPath.database.page}/company/:id/:menuId`, <DatabasePageLazy />, userData, coreData)}
					{renderRoute(`${routerPath.database.page}/country/:id/:menuId`, <CountryPageLazy />, userData, coreData)}

					{/* Benchmark */}
					{renderRoute(`${routerPath.benchmark.page}`, <BenchmarkPageLazy />, userData, coreData)}

					{/* Portfolio analyzer */}
					{/* saved=1 this is saved Analysis, saved=0 this is unsaved Analysis */}
					{renderRoute(`${routerPath.portfolio.page}${routerPath.portfolio.portfolio}/:id/:saved`, <PortfolioPageLazy />, userData, coreData)}
					{renderRoute(`${routerPath.portfolio.page}${routerPath.portfolio.portfolioList}`, <PortfolioViewPageLazy />, userData, coreData)}
					{renderRoute(`${routerPath.portfolio.page}${routerPath.portfolio.upload}/:id`, <PortfolioCreatePageLazy />, userData, coreData)}

					{/* Funds */}
					{renderRoute(`${routerPath.funds.page}${routerPath.funds.fund}/:id`, <FundPageLazy />, userData, coreData)}

					<Route
						path="*"
						element={<NotFoundPage />}
					/>
				</Routes>
			</Suspense>
		</BrowserRouter>
	);
};
